import { Controller } from "@hotwired/stimulus";
import { weeklyFirstN, monthlyFirstN, isoDate } from "./helpers/recurrence_occurrences";
import { allInPrice, netPayout } from "../utils/ticket_fees";

// Shared loading/gating behavior for the onboarding "details" and "signup"
// step forms (they share the step styling).
//
// - If `orgName` + `submit` targets are present, the submit button is disabled
//   until the gated field has a value (Step 3: only the host name is required).
// - On submit, the button shows a spinner and (for the signup step) cycles
//   through progress messages while the account + event are generated behind
//   the scenes — the request takes a few seconds, so the staged feedback
//   reassures the user something is happening. Only fires once the form passes
//   native HTML5 validation.
export default class extends Controller {
  static targets = ["form", "orgName", "submit", "website", "onlineToggle", "addressField",
                    "modelRadio", "priceField", "priceInput", "priceLabel", "priceHint", "priceHintText", "priceRequiredMark",
                    "absorbFeeRadio", "feeNote", "feeNoteText", "feeNoteSub",
                    "formatRadio", "recurrence", "recurrenceRadio", "weeklyDays",
                    "monthlyRule", "monthlyRuleSelect",
                    "occurrences", "occurrenceCount", "endsOn",
                    "startDate", "endDate", "endDateField", "endDateError", "startTime", "endTime", "endTimeError", "whenLabel", "whenHint",
                    "overnightNote", "overnightNoteText",
                    "shortMonthNote", "shortMonthNoteText"];
  static values = {
    loadingLabel: String,
    loadingStages: Array,
    overnightNote: String,
    shortMonthCallout: String,
    maxSpanDays: { type: Number, default: 31 },
    priceLabel: String,
    priceHint: String,
    donationLabel: String,
    donationHint: String,
    // Fee rates from config/initializers/constants.rb via the view — the only
    // way rates reach JS. Deliberately no numeric defaults (a silent fallback
    // that disagrees with the server is the bug this line exists to prevent).
    stubFeePercent: Number,
    stubFeeDollar: Number,
    stripeFeePercent: Number,
    stripeFeeCents: Number,
  };

  // Forgive a bare domain (e.g. "myorg.com") by prepending https:// so the
  // url field validates and the LLM gets a usable URL.
  normalizeWebsite() {
    if (!this.hasWebsiteTarget) return;
    const v = this.websiteTarget.value.trim();
    if (v && !/^https?:\/\//i.test(v)) {
      this.websiteTarget.value = `https://${v}`;
    }
  }

  // Single-day events end the same day, so the end time must be after the start
  // time. The time fields are 12-hour text comboboxes ("10:00 AM"), so we parse
  // to minutes and flag (highlight red + show a message) an end time that isn't
  // after the start — keeping the value so the user can see/fix it. Only enforced
  // for single day (multi-day/recurring end on a later date). The server-side
  // end_must_be_after_start validation is the backstop.
  constrainEndTime() {
    if (!this.hasStartTimeTarget || !this.hasEndTimeTarget) return;
    // An end clock at/before the start clock is always valid now: for single-day
    // it means the event runs overnight (e.g. 10 PM → 1 AM), and for multi-day /
    // recurring the block ends on a later date. The server rolls ends_at to the
    // next day. So the end time is never flagged on the clock axis — only the
    // (separate) multi-day end-DATE rule applies.
    this.setEndTimeInvalid(false);
    this.updateOvernightNote();
  }

  // Show an inline "ends the next day" notice when the end clock is at/before the
  // start clock — the event runs past midnight. Makes the overnight edge case
  // explicit so the organizer knows it's intentional. Hidden otherwise.
  updateOvernightNote() {
    if (!this.hasOvernightNoteTarget) return;
    const start = this.minutesFromLabel(this.startTimeTarget.value);
    const end = this.minutesFromLabel(this.endTimeTarget.value);
    const overnight = start != null && end != null && end <= start;

    this.overnightNoteTarget.hidden = !overnight;
    if (overnight && this.hasOvernightNoteTextTarget) {
      const template = this.hasOvernightNoteValue ? this.overnightNoteValue : "Ends the next day at {end}.";
      this.overnightNoteTextTarget.textContent =
        template.replace("{end}", this.endTimeTarget.value.trim());
    }
  }

  // Toggle the end-time error state: red input border + inline message.
  setEndTimeInvalid(invalid) {
    this.endTimeTarget.classList.toggle("is-invalid", invalid);
    this.endTimeTarget.setAttribute("aria-invalid", invalid ? "true" : "false");
    if (this.hasEndTimeErrorTarget) this.endTimeErrorTarget.hidden = !invalid;
  }

  // A MULTI-DAY event must span more than one day. If the organizer picks the
  // same (or earlier) end date, flag it — a same-day "multi-day" event is really
  // single-day and would generate a spurious extra occurrence. Only enforced for
  // the multi-day format (single ends same day; recurring derives its end date).
  validateEndDate() {
    if (!this.hasEndDateTarget) return;
    const multiDay =
      this.hasFormatRadioTarget &&
      this.formatRadioTargets.some((r) => r.checked && r.value === "multiple_event");

    // Keep the date picker's max in sync so the calendar itself blocks too-long
    // spans (MAX_MULTI_DAY_SPAN_DAYS inclusive of both endpoints).
    let reason = null;
    if (multiDay && this.hasStartDateTarget) {
      const start = this.startDateTarget.value;
      const end = this.endDateTarget.value;
      if (start) {
        const max = this.addDays(start, this.maxSpanDaysValue - 1);
        this.endDateTarget.max = max;
        if (start && end) {
          if (end <= start) reason = "same";
          else if (end > max) reason = "tooLong";
        }
      }
    } else if (this.hasEndDateTarget) {
      this.endDateTarget.removeAttribute("max");
    }
    this.setEndDateInvalid(reason);
    this.validate(); // keep the submit button's enabled state in sync
  }

  // ISO "YYYY-MM-DD" + N days -> ISO "YYYY-MM-DD" (local, no UTC shift).
  addDays(iso, days) {
    const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
    const dt = new Date(y, m - 1, d + days);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  }

  setEndDateInvalid(reason) {
    const invalid = !!reason;
    this.endDateTarget.classList.toggle("is-invalid", invalid);
    this.endDateTarget.setAttribute("aria-invalid", invalid ? "true" : "false");
    if (this.hasEndDateErrorTarget) {
      this.endDateErrorTarget.hidden = !invalid;
      if (invalid) {
        const ds = this.endDateErrorTarget.dataset;
        this.endDateErrorTarget.textContent =
          reason === "tooLong" ? (ds.tooLong || ds.sameDay || "") : (ds.sameDay || "");
      }
    }
    this._endDateInvalid = invalid;
  }

  // "10:30 PM" / "22:30" / "Midnight" / "Noon" -> minutes since midnight, or null.
  minutesFromLabel(raw) {
    if (!raw) return null;
    const s = raw.trim().toLowerCase();
    if (s === "midnight") return 0;
    if (s === "noon") return 720;
    const m = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    if (m[3] === "pm" && h !== 12) h += 12;
    if (m[3] === "am" && h === 12) h = 0;
    return h * 60 + min;
  }

  // Default the end date to the start date (until the organizer sets one), and
  // keep the end date's minimum at/after the start date.
  syncEndDate() {
    if (!this.hasStartDateTarget || !this.hasEndDateTarget) return;
    const start = this.startDateTarget.value;
    if (!start) return;
    this.endDateTarget.min = start;
    if (!this.endDateTarget.value || this.endDateTarget.value < start) {
      this.endDateTarget.value = start;
    }
    // The start date moved, so re-check the multi-day "end after start" rule.
    this.validateEndDate();
  }

  // Price/capacity are whole numbers. type="number" still lets users type "e",
  // ".", "+", "-", so we use a text input and keep it digits-only: block
  // non-digit characters as they're typed, and strip anything else (e.g. pasted
  // or autofilled) on input.
  blockNonDigits(event) {
    if (event.data && /\D/.test(event.data)) event.preventDefault();
  }

  digitsOnly(event) {
    const el = event.target;
    const cleaned = el.value.replace(/\D/g, "");
    if (cleaned !== el.value) el.value = cleaned;
  }

  // Online-event toggle: hide the physical-address field (and clear it) when on.
  toggleOnline() {
    if (!this.hasAddressFieldTarget || !this.hasOnlineToggleTarget) return;
    const online = this.onlineToggleTarget.checked;
    this.addressFieldTarget.classList.toggle("d-none", online);
    if (online) this.addressFieldTarget.value = "";
  }

  // Recurrence sub-options: only relevant when the "Recurring" format is picked.
  // Show the Weekly/Monthly toggle then, and hide it (resetting state) otherwise
  // so non-recurring events don't submit stray recurrence params.
  toggleRecurrence() {
    if (!this.hasFormatRadioTarget) return;
    const recurring = this.formatRadioTargets.some(
      (r) => r.checked && r.value === "recurring_event"
    );
    if (this.hasRecurrenceTarget) this.recurrenceTarget.hidden = !recurring;
    // The "Number of events" picker (and derived "Ends on" date) is recurring-only.
    if (this.hasOccurrencesTarget) {
      this.occurrencesTarget.hidden = !recurring;
      if (recurring) this.updateOccurrences();
    }
    this.toggleWeeklyDays();
    this.toggleEndDate();
    this.constrainEndTime();
    this.updateWhenLabel();
  }

  // Money model (free / paid / donation) drives the price field: hidden for
  // free, shown for paid (required) and donation (optional suggested amount).
  // Label/hint swap between "ticket price" and "suggested amount". Required
  // state feeds validate() so a paid event can't submit without a price.
  toggleModel() {
    if (!this.hasModelRadioTarget || !this.hasPriceFieldTarget) return;
    const checked = this.modelRadioTargets.find((r) => r.checked);
    const model = checked ? checked.value : "free";
    const collects = model === "paid" || model === "donation";

    this.priceFieldTarget.hidden = !collects;
    // Don't submit a stray price for a free event.
    if (!collects && this.hasPriceInputTarget) this.priceInputTarget.value = "";

    const donation = model === "donation";
    if (this.hasPriceLabelTarget) {
      this.priceLabelTarget.textContent = donation
        ? (this.donationLabelValue || this.priceLabelValue)
        : this.priceLabelValue;
    }
    // Write hint copy into the inner text span only — the icon sibling in the
    // wrapper must survive donation/paid swaps and hide/show cycles.
    if (this.hasPriceHintTextTarget) {
      this.priceHintTextTarget.textContent = donation
        ? (this.donationHintValue || this.priceHintValue)
        : this.priceHintValue;
    }
    // Price is required for paid only (donation amount is a suggestion).
    const priceRequired = model === "paid";
    if (this.hasPriceInputTarget) this.priceInputTarget.required = priceRequired;
    if (this.hasPriceRequiredMarkTarget) this.priceRequiredMarkTarget.hidden = !priceRequired;
    this.validate();
    this.updateFeeNote();
  }

  // Live fee line under the price field (see the style guide's "Fee choice at
  // the price ask"): computed amounts only — never rates, which go stale
  // against the constants. Paid swaps the static hint for the computed line
  // once a valid price exists (one slot, no stacking); donation shows a static
  // line per selection (the donor picks the amount, so there's no single total)
  // and keeps the hint — it carries the "leave blank" guidance, a separate job.
  updateFeeNote() {
    if (!this.hasFeeNoteTarget || !this.hasFeeNoteTextTarget) return;
    const checked = this.hasModelRadioTarget ? this.modelRadioTargets.find((r) => r.checked) : null;
    const model = checked ? checked.value : "free";
    const absorb = this.absorbFeeRadioTargets.some((r) => r.checked && r.value === "true");
    const ds = this.feeNoteTarget.dataset;

    if (model === "donation") {
      this.renderFeeNote(absorb ? ds.donationYouPay : ds.donationBuyerPays, {});
      if (this.hasFeeNoteSubTarget) this.feeNoteSubTarget.textContent = "";
      this.feeNoteTarget.hidden = false;
      if (this.hasPriceHintTarget) this.priceHintTarget.hidden = false;
      return;
    }

    const price = parseInt(this.hasPriceInputTarget ? this.priceInputTarget.value : "", 10);
    const valid = model === "paid" && Number.isFinite(price) && price > 0;
    this.feeNoteTarget.hidden = !valid;
    if (this.hasPriceHintTarget) this.priceHintTarget.hidden = valid;
    if (!valid) return;

    const consts = {
      stubFeePercent: this.stubFeePercentValue,
      stubFeeDollar: this.stubFeeDollarValue,
      stripeFeePercent: this.stripeFeePercentValue,
      stripeFeeCents: this.stripeFeeCentsValue,
    };
    const amounts = {
      price: this.formatUsd(price),
      total: this.formatUsd(allInPrice(price, consts)),
      net: this.formatUsd(netPayout(price, consts)),
    };
    this.renderFeeNote(absorb ? ds.youPay : ds.buyerPays, amounts);
    if (this.hasFeeNoteSubTarget) {
      this.feeNoteSubTarget.textContent = absorb ? ds.youPaySub : ds.buyerPaysSub;
    }
  }

  // Fill a locale template's {price}/{total}/{net} placeholders, bolding the
  // amounts. Built as DOM nodes so the (i18n) copy is inserted as text, never
  // parsed as HTML.
  renderFeeNote(template, amounts) {
    const nodes = (template || "").split(/(\{price\}|\{total\}|\{net\})/).map((part) => {
      const m = part.match(/^\{(price|total|net)\}$/);
      if (m && amounts[m[1]] != null) {
        const b = document.createElement("b");
        b.textContent = amounts[m[1]];
        return b;
      }
      return document.createTextNode(part);
    });
    this.feeNoteTextTarget.replaceChildren(...nodes);
  }

  formatUsd(amount) {
    return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
  }

  // The date/time row means something different per format: single day = the
  // event itself; multi-day = the FIRST day plus the hours every generated day
  // uses; recurring = the FIRST occurrence. Swap the label and show a hint for
  // the repeat formats so the intent is explicit.
  updateWhenLabel() {
    if (!this.hasWhenLabelTarget) return;
    const checked = this.hasFormatRadioTarget
      ? this.formatRadioTargets.find((r) => r.checked)?.value
      : null;
    const el = this.whenLabelTarget;
    const labels = {
      multiple_event: el.dataset.whenMultiLabel,
      recurring_event: el.dataset.whenRecurringLabel,
    };
    el.textContent = labels[checked] || el.dataset.whenLabel;

    if (this.hasWhenHintTarget) {
      const hint = this.whenHintTarget;
      const hints = {
        multiple_event: hint.dataset.multiHint,
        recurring_event: hint.dataset.recurringHint,
      };
      hint.textContent = hints[checked] || "";
      hint.hidden = !hints[checked];
    }
  }

  // The manual end DATE row is only for MULTI-DAY events. Single day ends the
  // same day; recurring derives its end date from the number-of-events picker, so
  // the manual field is hidden for both.
  toggleEndDate() {
    if (!this.hasEndDateFieldTarget || !this.hasFormatRadioTarget) return;
    const multiDay = this.formatRadioTargets.some(
      (r) => r.checked && r.value === "multiple_event"
    );
    this.endDateFieldTarget.hidden = !multiDay;
    if (!multiDay) {
      this.syncEndDate(); // keep end date pinned to start otherwise
      this.setEndDateInvalid(false); // clear any multi-day error when leaving the format
      this.validate();
    } else {
      this.validateEndDate(); // re-check now that multi-day requires end > start
    }
  }

  // Day-of-week chips only make sense for a weekly cadence — show them when
  // Weekly is selected (and the recurrence block is visible), hide otherwise.
  // The monthly "Repeat on" picker is the mirror: shown for a monthly cadence.
  toggleWeeklyDays() {
    const recurring = this.hasRecurrenceTarget && !this.recurrenceTarget.hidden;
    const cadence =
      recurring && this.hasRecurrenceRadioTarget
        ? this.recurrenceRadioTargets.find((r) => r.checked)?.value
        : null;

    if (this.hasWeeklyDaysTarget) this.weeklyDaysTarget.hidden = cadence !== "weekly";
    if (this.hasMonthlyRuleTarget) {
      this.monthlyRuleTarget.hidden = cadence !== "monthly";
      if (cadence === "monthly") {
        this.updateMonthlyOptions();
        this.updateShortMonthNote();
      } else if (this.hasShortMonthNoteTarget) {
        this.shortMonthNoteTarget.hidden = true;
      }
    }
    // The derived end date depends on cadence (weeks vs months), so refresh it.
    this.updateOccurrences();
  }

  // Recurring "Number of events" -> derived end date. Compute the REAL Nth
  // occurrence across all selected weekday chips (or the monthly rule), exactly
  // mirroring OnboardingEventGenerator#recurring_end_date and the count-bounded
  // server generation, then show "Ends on <date>" and sync the hidden end_date.
  updateOccurrences() {
    if (!this.hasOccurrencesTarget || this.occurrencesTarget.hidden) return;
    if (!this.hasEndsOnTarget || !this.hasStartDateTarget) return;

    const raw = this.startDateTarget.value;
    const [y, mo, d] = (raw || "").split("-").map((n) => parseInt(n, 10));
    if (!y || !mo || !d) { this.endsOnTarget.textContent = ""; return; }

    const start = new Date(y, mo - 1, d);
    const count = this.occurrenceCountValue();
    const occurrences = this.recurringOccurrences(start, count);
    const last = occurrences[occurrences.length - 1];
    if (!last) { this.endsOnTarget.textContent = ""; return; }

    const label = last.toLocaleDateString("default", { month: "long", day: "numeric", year: "numeric" });
    this.endsOnTarget.textContent = `Ends on ${label}`;

    // Keep the (hidden, for recurring) end_date field in sync with the real last
    // occurrence so the attendance-days preview bounds its window correctly.
    if (this.hasEndDateTarget) this.endDateTarget.value = isoDate(last);
  }

  // The first N occurrence dates for the currently-selected recurrence, mirroring
  // the server. Weekly: across the selected chips (falling back to the start
  // date's weekday when none picked). Monthly: from the chosen "Repeat on" rule.
  recurringOccurrences(start, count) {
    const monthly =
      this.hasRecurrenceRadioTarget &&
      this.recurrenceRadioTargets.some((r) => r.checked && r.value === "monthly");

    if (monthly) {
      const rule = this.hasMonthlyRuleSelectTarget ? this.monthlyRuleSelectTarget.value : "";
      return monthlyFirstN(start, rule, count);
    }

    let days = this.selectedWeekdays();
    if (days.length === 0) {
      days = [["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][start.getDay()]];
    }
    return weeklyFirstN(start, days, count);
  }

  // Lowercase names of the checked weekday chips (recurring_days[]).
  selectedWeekdays() {
    const scope = this.hasWeeklyDaysTarget ? this.weeklyDaysTarget : this.element;
    return Array.from(scope.querySelectorAll('input[name="recurring_days[]"]:checked')).map((cb) => cb.value);
  }

  // Chosen occurrence count, clamped to the server's allowed range (1..12) so a
  // devtools-tampered value can't drive the occurrence walk into a huge loop.
  occurrenceCountValue() {
    const n = this.hasOccurrenceCountTarget ? parseInt(this.occurrenceCountTarget.value, 10) : 3;
    if (!Number.isFinite(n) || n < 1) return 3;
    return Math.min(n, 12);
  }

  // Populate the monthly "Repeat on" select with the two cadence options derived
  // from the chosen first-event date: "Every Nth of the month" (day_of_month) and
  // "Every <nth> <weekday> of the month" (day_of_week). Mirrors the manual editor's
  // toggle_event_frequency_controller so both flows produce the same rule strings.
  updateMonthlyOptions() {
    if (!this.hasMonthlyRuleSelectTarget || !this.hasStartDateTarget) return;
    const raw = this.startDateTarget.value;
    if (!raw) return;
    const [y, mo, d] = raw.split("-").map((n) => parseInt(n, 10));
    if (!y || !mo || !d) return;
    const date = new Date(y, mo - 1, d); // local date (avoid UTC off-by-one)
    if (isNaN(date)) return;

    const dayOfMonth = date.getDate();
    const weekday = date.toLocaleString("default", { weekday: "long" }).toLowerCase();
    const weekOfMonth = Math.ceil(dayOfMonth / 7);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const isLastWeek = lastDay - dayOfMonth < 7;

    const domRule = `day_of_month(${dayOfMonth})`;
    const dowRule = isLastWeek
      ? `day_of_week(:${weekday} => [-1])`
      : `day_of_week(:${weekday} => [${weekOfMonth}])`;

    const ordinalDay = `${dayOfMonth}${this.ordinalSuffix(dayOfMonth)}`;
    const weekdayLabel = weekday.charAt(0).toUpperCase() + weekday.slice(1);
    const dowLabel = isLastWeek
      ? `Every last ${weekdayLabel} of the month`
      : `Every ${this.ordinalText(weekOfMonth)} ${weekdayLabel} of the month`;

    const previous = this.monthlyRuleSelectTarget.value;
    const select = this.monthlyRuleSelectTarget;
    select.innerHTML = "";
    [
      [domRule, `Every ${ordinalDay} of the month`],
      [dowRule, dowLabel],
    ].forEach(([value, text]) => {
      const opt = document.createElement("option");
      opt.value = value;
      opt.text = text;
      if (value === previous) opt.selected = true;
      select.appendChild(opt);
    });
    this.updateShortMonthNote();
  }

  // Show when the selected monthly rule is day_of_month(N) with N >= 29.
  updateShortMonthNote() {
    if (!this.hasShortMonthNoteTarget || !this.hasShortMonthNoteTextTarget) return;
    if (!this.hasMonthlyRuleSelectTarget || !this.hasMonthlyRuleTarget || this.monthlyRuleTarget.hidden) {
      this.shortMonthNoteTarget.hidden = true;
      return;
    }

    const rule = this.monthlyRuleSelectTarget.value || "";
    const dom = rule.match(/day_of_month\((\d{1,2})\)/);
    const day = dom ? parseInt(dom[1], 10) : null;
    const show = day != null && day >= 29;

    this.shortMonthNoteTarget.hidden = !show;
    if (!show) return;

    const ordinalDay = `${day}${this.ordinalSuffix(day)}`;
    const template = this.hasShortMonthCalloutValue
      ? this.shortMonthCalloutValue
      : "Months without a %{day} will use the last day of the month.";
    this.shortMonthNoteTextTarget.textContent = template.replace("%{day}", ordinalDay);
  }

  ordinalSuffix(day) {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  }

  ordinalText(week) {
    return ["", "First", "Second", "Third", "Fourth", "Fifth"][week] || "";
  }

  connect() {
    this.validate();
    this.toggleModel();
    this.toggleRecurrence();
    this.formTarget.addEventListener("submit", this.onSubmit.bind(this));
    // Turbo fires submit-end after the request resolves; if the server rejected
    // it (e.g. duplicate email), reset the button so it isn't stuck loading.
    this._onSubmitEnd = (e) => { if (!e.detail?.success) this.resetButton(); };
    this.formTarget.addEventListener("turbo:submit-end", this._onSubmitEnd);
  }

  disconnect() {
    if (this._stageTimer) clearInterval(this._stageTimer);
    this.formTarget.removeEventListener("turbo:submit-end", this._onSubmitEnd);
  }

  // Optional gating — only when both targets exist.
  validate() {
    if (!this.hasSubmitTarget) return;
    // Host name is required only when its field is shown (new organizer).
    const nameMissing =
      this.hasOrgNameTarget && this.orgNameTarget.value.trim().length === 0;
    // A paid event needs a price before it can be submitted (mirrors the
    // server-side gate in HomeController#onboarding_start).
    const priceMissing =
      this.hasPriceInputTarget &&
      this.priceInputTarget.required &&
      this.priceInputTarget.value.trim().length === 0;
    this.submitTarget.disabled =
      nameMissing || priceMissing || this._endDateInvalid === true;
  }

  onSubmit(event) {
    // Re-check the end time and block submit while it's flagged invalid, so the
    // user fixes it here rather than bouncing off the server-side validation.
    this.constrainEndTime();
    if (this.hasEndTimeTarget && this.endTimeTarget.classList.contains("is-invalid")) {
      event.preventDefault();
      this.endTimeTarget.focus();
      return;
    }

    // Respect native validation (required email/password, etc.) — don't show
    // the loading state if the browser is going to block the submit.
    if (!this.formTarget.checkValidity()) return;

    const button = this.hasSubmitTarget
      ? this.submitTarget
      : this.formTarget.querySelector('[type="submit"]');
    if (!button) return;

    // Remember the original button TEXT so we can restore it if the submit fails
    // server-side. Capturing text (not innerHTML) avoids round-tripping any DOM
    // that may have been clobbered into the button node.
    this._loadingButton = button;
    this._originalText = button.tagName === "INPUT" ? button.value : button.textContent;

    button.disabled = true;
    button.classList.add("is-loading");
    button.setAttribute("aria-busy", "true");

    const stages = this.hasLoadingStagesValue && this.loadingStagesValue.length
      ? this.loadingStagesValue
      : [this.hasLoadingLabelValue ? this.loadingLabelValue : "Creating…"];

    this.setButtonLabel(button, stages[0]);

    // Step through the remaining stages; hold on the last one until navigation.
    if (stages.length > 1) {
      let i = 1;
      this._stageTimer = setInterval(() => {
        this.setButtonLabel(button, stages[i]);
        i += 1;
        if (i >= stages.length) clearInterval(this._stageTimer);
      }, 1600);
    }
  }

  setButtonLabel(button, label) {
    if (button.tagName === "INPUT") {
      button.value = label;
      return;
    }
    // Build the spinner + label via DOM nodes so the (i18n) label is inserted as
    // text, never parsed as HTML.
    const spinner = document.createElement("span");
    spinner.className = "redirect-spinner";
    spinner.setAttribute("aria-hidden", "true");
    button.replaceChildren(spinner, document.createTextNode(label));
  }

  // Restore the submit button after a failed submission so the user can fix
  // the error (e.g. an email already in use) and try again.
  resetButton() {
    if (this._stageTimer) clearInterval(this._stageTimer);
    const button = this._loadingButton;
    if (!button) return;

    button.classList.remove("is-loading");
    button.removeAttribute("aria-busy");
    button.disabled = false;
    if (this._originalText != null) {
      if (button.tagName === "INPUT") button.value = this._originalText;
      else button.textContent = this._originalText;
    }
    this._loadingButton = null;
  }
}
